import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase.js'

const FORCE_PREMIUM = false
const BASE_URL = '/api/me/quota'

async function fetchQuota() {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) return null
  const res = await fetch(BASE_URL, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  })
  if (!res.ok) return null
  return res.json()
}

const QUOTA_CACHE_KEY = 'level_quota_cache'

function getCached() {
  try {
    const raw = sessionStorage.getItem(QUOTA_CACHE_KEY)
    if (!raw) return null
    const { data, ts } = JSON.parse(raw)
    if (Date.now() - ts > 5 * 60 * 1000) return null  // 5 min TTL
    return data
  } catch { return null }
}

function setCached(data) {
  try { sessionStorage.setItem(QUOTA_CACHE_KEY, JSON.stringify({ data, ts: Date.now() })) } catch {}
}

export function useQuota() {
  const cached = getCached()
  const [quota, setQuota] = useState(cached)
  const [loading, setLoading] = useState(cached === null)

  const load = useCallback(async () => {
    if (!getCached()) setLoading(true)
    try {
      const data = await fetchQuota()
      setQuota(data ?? null)
      if (data) setCached(data)
    } catch { /* ignore */ } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (FORCE_PREMIUM) {
      setQuota({ is_premium: true, analyses_this_week: 0, limit: 1, remaining: null })
      setLoading(false)
      return
    }

    load()

    // Refetch when user returns to tab (e.g. after granting trial in Admin)
    function onVisibility() {
      if (document.visibilityState === 'visible') load()
    }
    document.addEventListener('visibilitychange', onVisibility)

    // Reset + refetch on auth change (login/logout/switch account)
    // No reseteamos quota a null directamente porque provoca un flash de "Acceso denegado"
    // en Safari/Mac cuando el SO reenfoca la ventana y Supabase renueva el token.
    // Invalidamos el cache para que load() active setLoading(true) y muestre el spinner.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      try { sessionStorage.removeItem(QUOTA_CACHE_KEY) } catch {}
      load()
    })

    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      subscription.unsubscribe()
    }
  }, [load])

  return {
    isPremium: quota?.is_premium ?? false,
    isAdmin: quota?.is_admin ?? false,
    isTeacher: quota?.is_teacher ?? false,
    trialActive: quota?.trial_active ?? false,
    trialExpiresAt: quota?.trial_expires_at ?? null,
    used: quota?.analyses_this_week ?? 0,
    limit: quota?.limit ?? 1,
    remaining: quota?.remaining ?? 1,
    loading,
    refetch: load,
  }
}
